import { ReactNode, UIEvent, useState } from 'react';

import type { AlfaPlatform } from './alfaApp';
import styles from './AlfaQrInvoice.module.scss';
import {
  CopyActionIcon,
  DownloadQrIcon,
  GalleryIcon,
  MoreIcon,
  SaveImageIcon,
  ScanIcon,
} from './icons';

type HowToPayProps = {
  platform: AlfaPlatform | null;
  miniQr: ReactNode;
};

const SLIDES_COUNT = 3;

/**
 * P2P-3116: «Как оплатить» на телефоне - карусель из трёх слайдов с точками: скачайте QR, откройте
 * сканер, выберите QR из галереи. На iOS первый слайд - меню «Поделиться» вместо экрана загрузки.
 */
export const HowToPay = ({ platform, miniQr }: HowToPayProps) => {
  const [activeSlide, setActiveSlide] = useState(0);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollLeft, scrollWidth, clientWidth } = event.currentTarget;
    const maxScroll = scrollWidth - clientWidth;

    setActiveSlide(maxScroll > 0 ? Math.round((scrollLeft / maxScroll) * (SLIDES_COUNT - 1)) : 0);
  };

  return (
    <div className={styles.howTo}>
      <div className={styles.howToTitle}>
        <b>Как оплатить</b>
        <span>листайте →</span>
      </div>
      <div className={styles.slides} onScroll={handleScroll} data-testid="alfa-how-to-slides">
        <div className={styles.slide}>
          <div className={styles.slideCaption}>
            <span className={styles.stepNumber}>1</span>Скачайте QR
          </div>
          {platform === 'ios' ? (
            <div className={`${styles.screen} ${styles.shareSheet}`} aria-hidden>
              <div className={styles.shareFile}>
                <div className={styles.sharePng}>PNG</div>
                <div>
                  <b>qr-oplata.png</b>Формат PNG
                </div>
              </div>
              <div className={styles.shareApps}>
                <i />
                <i />
                <i />
                <i />
              </div>
              <div className={styles.shareActions}>
                <div className={styles.shareAction}>
                  <span className={styles.shareIcon}>
                    <CopyActionIcon />
                  </span>
                  Скопировать
                </div>
                <div className={`${styles.shareAction} ${styles.shareActionOn}`}>
                  <span className={`${styles.shareIcon} ${styles.ring}`}>
                    <SaveImageIcon />
                  </span>
                  Сохранить изображение
                </div>
                <div className={styles.shareAction}>
                  <span className={styles.shareIcon}>
                    <MoreIcon />
                  </span>
                  Ещё
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.screen} aria-hidden>
              <div className={styles.miniQr}>{miniQr}</div>
              <div className={styles.miniDownload}>
                <DownloadQrIcon />
                Скачать QR
              </div>
            </div>
          )}
        </div>
        <div className={styles.slide}>
          <div className={styles.slideCaption}>
            <span className={styles.stepNumber}>2</span>Откройте сканер
          </div>
          <div className={styles.screen} aria-hidden>
            <div className={styles.screenRow}>
              <div className={styles.avatar} />
              <span className={styles.screenName}>Иван ›</span>
            </div>
            <div className={styles.account}>
              <div>
                Текущий счёт<b>15 240 ₽</b>
              </div>
              <div className={styles.chip} />
            </div>
            <div className={styles.screenRow} style={{ marginTop: 10 }}>
              <div className={styles.search}>⌕ Поиск</div>
              <div className={styles.ring}>
                <ScanIcon />
              </div>
            </div>
            <div className={styles.tag}>Нажмите сюда</div>
          </div>
        </div>
        <div className={styles.slide}>
          <div className={styles.slideCaption}>
            <span className={styles.stepNumber}>3</span>Выберите QR из галереи
          </div>
          <div className={styles.screen} aria-hidden>
            <div className={styles.camera}>
              <div className={styles.cameraFrame} />
            </div>
            <div className={styles.cameraBar}>
              <div className={`${styles.ring} ${styles.ringSquare}`}>
                <GalleryIcon />
              </div>
              <span className={`${styles.tag} ${styles.tagInline}`}>Нажмите сюда</span>
              <div className={styles.shutter} />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.dots}>
        {Array.from({ length: SLIDES_COUNT }, (_, index) => (
          <i key={index} className={index === activeSlide ? styles.dotActive : undefined} />
        ))}
      </div>
    </div>
  );
};
